import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'

// GET /ready health probe. /ready is a core probe at ROOT (outside the /api
// namespace) — buildUrl skips the prefix for /health + /ready, so this
// call-site stays literally '/ready'. The key is a plain non-org tuple
// (platform-level resource, no ?orgId=).
const HEALTH_KEY = ['health-probe'] as const

export function HealthProbe() {
  const { t } = useTranslation()
  const q = useQuery({
    queryKey: HEALTH_KEY,
    queryFn: () => apiFetch<{ status: string }>('/ready'),
    refetchInterval: 30000,
    retry: 1,
  })
  return (
    <span className="motion-status inline-flex" role="status" aria-live="polite" aria-atomic="true">
      {q.isLoading ? (
        <Badge variant="outline">{t('common.healthStatus.checking')}</Badge>
      ) : q.isError ? (
        <Badge variant="danger" title={q.error instanceof Error ? q.error.message : 'error'}>
          {t('common.healthStatus.down')}
        </Badge>
      ) : q.data?.status === 'ok' ? (
        <Badge variant="success" title={t('common.healthStatus.status', { status: t('common.healthStatus.operational') })}>
          {t('common.healthStatus.operational')}
        </Badge>
      ) : (
        <Badge
          variant="warning"
          title={
            q.data?.status
              ? (t('common.healthStatus.status', { status: q.data.status }) as string)
              : t('common.healthStatus.unknown')
          }
        >
          {q.data?.status
            ? (t(`common.healthStatus.${q.data.status}`, { defaultValue: q.data.status }) as string)
            : t('common.healthStatus.unknown')}
        </Badge>
      )}
    </span>
  )
}
